import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Text } from '@salesforce/retail-react-app/app/components/shared/ui'
import { useUsid, useShopperOrdersMutation } from '@salesforce/commerce-sdk-react'
import { useCurrentBasket } from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    generateDomLoader,
    getEswConfigByKey,
    getEswSiteAccessTokenByKey,
    removeDomLoader,
    getCookie
} from '../../esw-helpers'
import { useShopperBasketsMutation } from '@salesforce/commerce-sdk-react'
import { useToast } from '@salesforce/retail-react-app/app/hooks/use-toast'
import { API_ERROR_MESSAGE } from '@salesforce/retail-react-app/app/constants'
import { InfoCircle } from '../../../components/icons'

export const EswCheckoutBtn = (props) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { site, locale } = useMultiSite()
    const navigate = useNavigation()
    const [isLoading, setIsLoading] = useState(false)
    const { variant, basketIdParam, checkOrderAble } = props
    const { usid } = useUsid()
    const { mutateAsync: createOrder } = useShopperOrdersMutation('createOrder')
    const { mutateAsync: updateBasket } = useShopperBasketsMutation('updateBasket')
    const { data: basket } = useCurrentBasket()
    const showToast = useToast()
    const { formatMessage } = useIntl()

    const handleEswCheckout = async () => {
        if (!locale.isSupportedByESW || !getEswConfigByKey('eswEshopworldModuleEnabled')) {
            navigate('/checkout')
            return
        }
        setIsLoading(true)
        let spinner = null
        if (locale.isSupportedByESW) {
            try {
                spinner = generateDomLoader()
                let accessToken = getEswSiteAccessTokenByKey(site?.id)
                let order
                const basketId = basketIdParam && basketIdParam.length > 0
                    ? basketIdParam
                    : basket?.basketId
                if (getEswConfigByKey('isEswEnabledEmbeddedCheckout') && accessToken) {
                    if (basketId) {
                        order = await updateBasket({
                            parameters: {
                                basketId
                            },
                            body: { c_eswPreOrderRequest: accessToken }
                        })
                    }
                } else {
                    order = await createOrder({
                        // We send the SLAS usid via this header. This is required by ECOM to map
                        // Einstein events sent via the API with the finishOrder event fired by ECOM
                        // when an Order transitions from Created to New status.
                        // Without this, various order conversion metrics will not appear on reports and dashboards
                        headers: { _sfdc_customer_id: usid },
                        body: {
                            basketId
                        }
                    })
                }
                if (
                    !order ||
                    !order.c_eswPreOrderResponse ||
                    !order.c_eswPreOrderResponse.redirectUrl
                ) {
                    throw new Error(API_ERROR_MESSAGE)
                }
                const eswSessionId = getCookie('esw.sessionid')

                if (eswSessionId) {
                    localStorage.setItem('userSessionId', eswSessionId)
                }
                if (order?.c_eswShopperAccessToken) {
                    let eswgetTopLevelDomain = getEswConfigByKey('eswgetTopLevelDomain')
                    let expirationDate = new Date()
                    expirationDate.setTime(expirationDate.getTime() + 3600 * 1000)
                    document.cookie =
                        'esw-shopper-access-token=' +
                        order.c_eswShopperAccessToken +
                        ';path=/;domain=' +
                        eswgetTopLevelDomain +
                        ';expires=' +
                        expirationDate.toUTCString() +
                        ';SameSite=None;Secure'
                }
                localStorage.setItem('esw.clientLastOrderId', order.orderNo)
                setTimeout(() => {
                    window.location.replace(order.c_eswPreOrderResponse.redirectUrl)
                }, 500)
            } catch (error) {
                if (spinner && spinner.childElementCount > 0) {
                    removeDomLoader(spinner)
                }
                showToast({
                    title: formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }
        }
    }

    const infoIcon = <InfoCircle width={'10px'} height={'10px'} verticalAlign="super" />
    const hoverTooltip = formatMessage({
        defaultMessage: 'ESW is Garage Clothing\'s preferred partner for international sales.',
        id: 'cart_cta.link.esw_checkout_disclaimer_hover_tooltip'
    })

    return (
        <>
            <Button
                as="button"
                onClick={handleEswCheckout}
                width={['95%', '95%', '95%', '100%']}
                marginTop={[6, 6, 2, 2]}
                mb={4}
                variant={variant}
                isDisabled={isLoading || (checkOrderAble && !basket.c_isOrderAbleBasket)}
                data-testid="esw-checkout-btn"
            >
                <FormattedMessage
                    defaultMessage="Proceed to Checkout"
                    id="cart_cta.link.checkout"
                />
            </Button>
            <Text fontSize="xs" color="gray.500">
                <FormattedMessage
                    defaultMessage='By proceeding to checkout, you will be redirected to the <hoverTag>ESW</hoverTag> checkout page where payment will be taken and your order fulfilled by ESW, our partner for international sales.'
                    id="cart_cta.link.esw_checkout_disclaimer"
                    values={{
                        hoverTag: (chunks) => (
                            <span title={hoverTooltip} style={{ textDecoration: 'underline', }}>
                                {chunks} {infoIcon}
                            </span>
                        )
                    }}
                />
            </Text>
        </>
    )
}

EswCheckoutBtn.propTypes = {
    variant: PropTypes.string.isRequired,
    basketIdParam: PropTypes.string,
    checkOrderAble: PropTypes.bool
}
